import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CircularProgress,
  InputAdornment,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material';

import Page from '@/components/Page';
import Stack from '@/components/Stack';
import { Body } from '@/components/Typography';
import Text from '@/elements/Text';
import {
  ShiftEventActor,
  ShiftEventBackfillFill,
  ShiftEventCheckin,
  ShiftEventCheckout,
  ShiftEventCompleted,
  ShiftEventDropReference,
  ShiftEventEntry,
  ShiftEventFilterOption,
  ShiftEventReminder,
  ShiftEventStarted,
  fetchShiftEventFilters,
  fetchShiftEvents,
} from '@/services/shiftEventsApi';

const PER_PAGE = 50;

// Figma tokens (GravyWork 2.0 Baseline — Operations log)
const GREEN = '#2e8722'; // accent-text / text-green
const GRAY = '#5b5b5b'; // secondary-dark-gray
const BLACK = '#1c1c1c'; // primary-black
const BORDER = '#dadada'; // accent-stroke / stroke-gray

// Renders "Today, 8:05 AM" / "Yesterday, ..." / "Jun 22, 8:05 AM" to match the
// design's relative-day timestamps.
const formatTimestamp = (value: string | null) => {
  if (!value) return '';
  const d = new Date(value);
  const time = d.toLocaleString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  if (sameDay(d, today)) return `Today, ${time}`;
  if (sameDay(d, yesterday)) return `Yesterday, ${time}`;
  const day = d.toLocaleString('en-US', { month: 'short', day: 'numeric' });
  return `${day}, ${time}`;
};

// The shift's scheduled window, e.g. "Jun 22, 8:30 AM – 4:30 PM".
const formatShiftWindow = (entry: ShiftEventEntry) => {
  const { shift } = entry;
  if (!shift?.start_at) return null;
  const start = new Date(shift.start_at);
  const date = start.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
  });
  const startTime = start.toLocaleString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
  if (!shift.end_at) return `${date}, ${startTime}`;
  const endTime = new Date(shift.end_at).toLocaleString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
  return `${date}, ${startTime} – ${endTime}`;
};

// Event types whose actor line carries an explicit action verb per the design,
// e.g. "Created by GravyWork admin, Today, 8:05 AM". Scoped to the four named in
// GRAVY-10208; other manual/worker events keep the bare "<actor>, <timestamp>"
// form until the remaining verbs are confirmed from the Figma.
const ACTOR_VERBS: Record<string, string> = {
  'shift.created': 'Created by',
  'shift.invitation.sent': 'Invited by',
  'shift.updated': 'Edited by',
  'shift.cancelled': 'Cancelled by',
};

// Right-hand actor line, e.g. "Created by GravyWork admin, Today, 8:05 AM". The
// action verb is prefixed for the mapped event types (ACTOR_VERBS); other rows
// read "<actor>, <timestamp>". System events with no human actor show only the
// timestamp (matching the Figma).
const formatActorLine = (entry: ShiftEventEntry) => {
  const timestamp = formatTimestamp(entry.created_at);
  const { actor } = entry;
  if (!actor || actor.type === 'system' || !actor.name) return timestamp;
  const verb = ACTOR_VERBS[entry.event_type];
  const actorText = verb ? `${verb} ${actor.name}` : actor.name;
  return timestamp ? `${actorText}, ${timestamp}` : actorText;
};

// A dot-separated metadata row (falsy parts dropped), matching the "•" list in
// the design.
const MetaRow = ({ parts }: { parts: Array<string | null | undefined> }) => {
  const items = parts.filter(Boolean) as string[];
  if (items.length === 0) return null;
  return (
    <Stack gap={8} wrap>
      {items.map((item, i) => (
        <Fragment key={i}>
          {i > 0 && (
            <span
              style={{
                width: 4,
                height: 4,
                borderRadius: '50%',
                backgroundColor: GRAY,
                flexShrink: 0,
              }}
            />
          )}
          <Text as="span" size="sm" css={{ color: GRAY }}>
            {item}
          </Text>
        </Fragment>
      ))}
    </Stack>
  );
};

// The primary line: "Banquet server/runner at The Connor Residences" with the
// client name bolded per the design. Falls back to the position alone, or the
// event's own description, when the shift carries no position/client.
const PrimaryLine = ({ entry }: { entry: ShiftEventEntry }) => {
  const { shift } = entry;
  if (shift?.position && shift.client_name) {
    return (
      <Text as="p" size="md" css={{ color: BLACK }}>
        {shift.position} at{' '}
        <Text as="span" size="md" weight="bold" css={{ color: BLACK }}>
          {shift.client_name}
        </Text>
      </Text>
    );
  }
  return (
    <Text as="p" size="md" css={{ color: BLACK }}>
      {shift?.position || entry.description}
    </Text>
  );
};

// Fill progress, e.g. "1/2 filled". Omitted when the counts are unavailable.
const formatFill = (entry: ShiftEventEntry) => {
  const { shift } = entry;
  if (shift?.slots_total == null || shift.slots_filled == null) return null;
  return `${shift.slots_filled}/${shift.slots_total} filled`;
};

// Cancellation-specific meta parts for a `shift.cancelled` row: reason, how far
// ahead of the shift it was cancelled, and the workers hired at cancel time.
// Returns [] for non-cancellation events (which carry no cancellation payload).
const cancellationParts = (entry: ShiftEventEntry): string[] => {
  const c = entry.cancellation;
  if (!c) return [];
  const parts: string[] = [];
  if (c.reason) parts.push(`Reason: ${c.reason}`);
  if (c.hours_before_shift) parts.push(c.hours_before_shift);
  if (c.hired_worker_count > 0) {
    const names = c.hired_workers
      .map((w) => w.name)
      .filter(Boolean) as string[];
    parts.push(
      names.length > 0
        ? `Hired: ${names.join(', ')}`
        : `${c.hired_worker_count} hired at cancellation`,
    );
  }
  return parts;
};

// Human labels for what sent an invitation (json_data.source). Falls back to a
// humanized form for any source not mapped here (see humanizeSource, below), so
// a future sender still reads cleanly instead of showing a raw snake_case token.
const INVITATION_SOURCE_LABELS: Record<string, string> = {
  system_first_responder: 'First Responder',
};

// Invitation-specific meta parts for a `shift.invitation.sent` row: what sent
// it, who was invited (names when known, otherwise a count) and the channel
// used. Returns [] for other events.
//
// GRAVY-10373: a First Responder invitation has no human sender, so the actor
// line shows only a timestamp and the row cannot answer "who sent this?".
// `source` is the payload's only record of it, which is why it LEADS the parts —
// it is the first question a senderless invitation raises. A manual invitation
// carries no source and keeps today's line exactly: its sender is already the
// row's actor, and repeating it here would just be noise.
const invitationParts = (entry: ShiftEventEntry): string[] => {
  const inv = entry.invitation;
  if (!inv) return [];
  const parts: string[] = [];
  if (inv.source) {
    parts.push(
      `Sent by: ${INVITATION_SOURCE_LABELS[inv.source] ?? humanizeSource(inv.source)}`,
    );
  }
  if (inv.invited_worker_count > 0) {
    const names = inv.invited_workers
      .map((w) => w.name)
      .filter(Boolean) as string[];
    const count = `${inv.invited_worker_count} worker${
      inv.invited_worker_count === 1 ? '' : 's'
    } invited`;
    parts.push(names.length > 0 ? `Invited: ${names.join(', ')}` : count);
  }
  if (inv.channel) parts.push(`Channel: ${inv.channel}`);
  return parts;
};

// A minute count as "45 min", "2h", or "2h 30m". Used for the expiry window.
const formatMinutes = (minutes: number): string => {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins === 0 ? `${hours}h` : `${hours}h ${mins}m`;
};

// Expiration-specific meta parts for a `shift.invitation.expired` row: which
// worker's invitation lapsed and the offer's expiry window. The event is
// system-triggered, so the worker is not on the actor line — surface it here.
// Returns [] for other events.
const expirationParts = (entry: ShiftEventEntry): string[] => {
  const exp = entry.expiration;
  if (!exp) return [];
  const parts: string[] = [];
  if (exp.worker)
    parts.push(`Worker: ${exp.worker.name ?? `#${exp.worker.id}`}`);
  if (exp.expiry_window_minutes != null) {
    parts.push(`Expiry window: ${formatMinutes(exp.expiry_window_minutes)}`);
  }
  return parts;
};

// Human labels for the hire source (json_data.approval_type). Falls back to the
// raw value for any source not mapped here.
const HIRE_SOURCE_LABELS: Record<string, string> = {
  claim: 'Claimed by worker',
  offer_accepted: 'Accepted invitation',
  request_accepted: 'Accepted request',
  manual: 'Manual hire',
  order: 'Order hire',
};

// Hire-specific meta parts for a `shift.worker.hired` row: the hired worker, the
// hire source, and the approving admin when present. For self-service hires the
// worker is also the row actor and there is no approver, so approved_by is
// omitted gracefully. Returns [] for other events.
const hireParts = (entry: ShiftEventEntry): string[] => {
  const h = entry.hire;
  if (!h) return [];
  const parts: string[] = [];
  if (h.worker) parts.push(`Worker: ${h.worker.name ?? `#${h.worker.id}`}`);
  if (h.approval_type) {
    parts.push(
      `Source: ${HIRE_SOURCE_LABELS[h.approval_type] ?? h.approval_type}`,
    );
  }
  if (h.approved_by?.name) parts.push(`Approved by: ${h.approved_by.name}`);
  return parts;
};

// Reason-specific meta parts for the three vacancy events — shift.worker.dropped,
// shift.invitation.declined, and worker.removed_from_shift — surfacing the
// affected worker and why they left (drop/decline/removal reason). The worker is
// shown because ops/system-triggered rows don't carry it on the actor line.
// Returns [] for other events.
// Human labels for the decline source (json_data.declined_via) on a
// shift.invitation.declined row. Falls back to a humanized form for any source
// not mapped here (see humanizeSource, defined below).
const DECLINE_VIA_LABELS: Record<string, string> = {
  decline_mobile: 'Mobile app',
  reject_v1: 'Reject (v1)',
  reject_v2: 'Reject (v2)',
};

const reasonParts = (entry: ShiftEventEntry): string[] => {
  const r = entry.reason;
  if (!r) return [];
  const parts: string[] = [];
  if (r.worker) parts.push(`Worker: ${r.worker.name ?? `#${r.worker.id}`}`);
  if (r.reason) parts.push(`Reason: ${r.reason}`);
  // Only declines carry a source; workers give no reason when declining, so this
  // is the auditable "how". Omitted (no blank label) for drop/removal or legacy.
  if (r.declined_via) {
    parts.push(
      `Via: ${DECLINE_VIA_LABELS[r.declined_via] ?? humanizeSource(r.declined_via)}`,
    );
  }
  return parts;
};

// Human labels for the pool a backfill re-invite drew from (json_data.pool_source).
// Falls back to the raw value for any source not mapped here.
const POOL_SOURCE_LABELS: Record<string, string> = {
  original: 'Original pool',
  expanded: 'Expanded pool',
};

// The causal-reference clause for a backfill row, e.g. "Following drop by Jane"
// or "Following removal of Jane". A self-drop reads "by <worker>"; an ops/system
// removal reads "of <worker>" (the removal was done to them). Null when there is
// no linked worker to name.
const followingClause = (following: ShiftEventDropReference): string => {
  const removal = following.event_type === 'worker.removed_from_shift';
  const verb = removal ? 'removal' : 'drop';
  const who =
    following.worker?.name ??
    (following.worker ? `#${following.worker.id}` : null);
  if (!who) return `Following ${verb}`;
  return removal ? `Following removal of ${who}` : `Following drop by ${who}`;
};

// Backfill-specific meta parts for a `shift.backfill.invite_sent` row: who was
// re-invited (names when known, otherwise a count), which pool the re-invite
// drew from, and the drop/removal it followed. The event is system-triggered, so
// recipients are surfaced here rather than on the actor line. Returns [] for
// other events.
const backfillParts = (entry: ShiftEventEntry): string[] => {
  const b = entry.backfill;
  if (!b) return [];
  const parts: string[] = [];
  if (b.recipient_worker_count > 0) {
    const names = b.recipient_workers
      .map((w) => w.name)
      .filter(Boolean) as string[];
    const count = `${b.recipient_worker_count} worker${
      b.recipient_worker_count === 1 ? '' : 's'
    } re-invited`;
    parts.push(names.length > 0 ? `Re-invited: ${names.join(', ')}` : count);
  }
  if (b.pool_source) {
    parts.push(`Pool: ${POOL_SOURCE_LABELS[b.pool_source] ?? b.pool_source}`);
  }
  if (b.following) parts.push(followingClause(b.following));
  return parts;
};

// Slot-fill meta parts for a `shift.backfill.slot_filled` row: who filled the
// vacated slot, who departed, how long the slot stood open, and the drop/removal
// it followed. The event is system-triggered and names both workers separately,
// so they are surfaced here rather than on the actor line. Returns [] for other
// events.
const backfillFillParts = (entry: ShiftEventEntry): string[] => {
  const f: ShiftEventBackfillFill | null = entry.backfill_fill;
  if (!f) return [];
  const parts: string[] = [];
  if (f.replacement_worker) {
    parts.push(
      `Replacement: ${
        f.replacement_worker.name ?? `#${f.replacement_worker.id}`
      }`,
    );
  }
  if (f.original_worker) {
    parts.push(
      `Original: ${f.original_worker.name ?? `#${f.original_worker.id}`}`,
    );
  }
  if (f.minutes_to_fill != null) {
    parts.push(`Time to fill: ${formatMinutes(f.minutes_to_fill)}`);
  }
  if (f.following) parts.push(followingClause(f.following));
  return parts;
};

// Human labels for how a worker checked in (json_data.check_in_method). Falls
// back to the raw value for any method not mapped here.
const CHECK_IN_METHOD_LABELS: Record<string, string> = {
  app: 'App check-in',
  manual_app: 'Manual app check-in',
  qr: 'QR check-in',
  ops_manual: 'Ops manual override',
  ops_enter_hours: 'Ops entered hours',
};

// Timing relative to the scheduled shift start: negative minutes are early,
// positive are late, zero is on time. Used for the check-in row.
const formatRelativeToStart = (minutes: number): string => {
  if (minutes === 0) return 'On time';
  const label = formatMinutes(Math.abs(minutes));
  return minutes < 0 ? `${label} early` : `${label} late`;
};

// Check-in meta parts for a `shift.worker.checked_in` row: how the worker
// checked in, how early/late they were, and whether their location was
// GPS-verified. The worker is surfaced because ops-initiated flows (ops_manual,
// ops_enter_hours) put the admin — not the worker — on the actor line. Returns
// [] for other events. Shared by all five check-in flows.
const checkinParts = (entry: ShiftEventEntry): string[] => {
  const c: ShiftEventCheckin | null = entry.checkin;
  if (!c) return [];
  const parts: string[] = [];
  if (c.worker) parts.push(`Worker: ${c.worker.name ?? `#${c.worker.id}`}`);
  if (c.check_in_method) {
    parts.push(
      `Method: ${
        CHECK_IN_METHOD_LABELS[c.check_in_method] ?? c.check_in_method
      }`,
    );
  }
  if (c.minutes_relative_to_start != null) {
    parts.push(formatRelativeToStart(c.minutes_relative_to_start));
  }
  if (c.location_verified != null) {
    parts.push(
      c.location_verified ? 'Location verified' : 'Location not verified',
    );
  }
  return parts;
};

// Human labels for how a worker checked out (json_data.check_out_method). Falls
// back to the raw value for any method not mapped here. Mirrors the check-in
// methods (the same five flows apply to check-out).
const CHECK_OUT_METHOD_LABELS: Record<string, string> = {
  app: 'App check-out',
  manual_app: 'Manual app check-out',
  qr: 'QR check-out',
  ops_manual: 'Ops manual override',
  ops_enter_hours: 'Ops entered hours',
  missing_checkout: 'Missing check-out fix',
};

// An hours value as "6h", "6h 30m", or "45m". Used for actual hours worked.
const formatHoursWorked = (hours: number): string => {
  const whole = Math.floor(hours);
  const mins = Math.round((hours - whole) * 60);
  if (whole === 0) return `${mins}m`;
  return mins === 0 ? `${whole}h` : `${whole}h ${mins}m`;
};

// Check-out meta parts for a `shift.worker.checked_out` row: how the worker
// checked out, the actual hours worked, and whether they left before the
// scheduled end. The worker is surfaced because ops-initiated flows (ops_manual,
// ops_enter_hours) put the admin — not the worker — on the actor line. Returns
// [] for other events. Shared by all check-out flows.
const checkoutParts = (entry: ShiftEventEntry): string[] => {
  const c: ShiftEventCheckout | null = entry.checkout;
  if (!c) return [];
  const parts: string[] = [];
  if (c.worker) parts.push(`Worker: ${c.worker.name ?? `#${c.worker.id}`}`);
  if (c.check_out_method) {
    parts.push(
      `Method: ${
        CHECK_OUT_METHOD_LABELS[c.check_out_method] ?? c.check_out_method
      }`,
    );
  }
  if (c.actual_hours_worked != null) {
    parts.push(`Hours worked: ${formatHoursWorked(c.actual_hours_worked)}`);
  }
  if (c.early_departure != null) {
    parts.push(c.early_departure ? 'Early departure' : 'Left on time');
  }
  return parts;
};

// Start-specific meta parts for a `shift.started` row: who was expected on the
// shift (names when known, otherwise a count) and the fill snapshot captured at
// start time. The event is system-emitted by the lifecycle sweep, so the
// expected workers are surfaced here rather than on the actor line. Returns []
// for other events.
const startedParts = (entry: ShiftEventEntry): string[] => {
  const s: ShiftEventStarted | null = entry.started;
  if (!s) return [];
  const parts: string[] = [];
  if (s.expected_worker_count > 0) {
    const names = s.expected_workers
      .map((w) => w.name)
      .filter(Boolean) as string[];
    const count = `${s.expected_worker_count} worker${
      s.expected_worker_count === 1 ? '' : 's'
    } expected`;
    parts.push(names.length > 0 ? `Expected: ${names.join(', ')}` : count);
  }
  if (s.slots_filled_count != null) {
    parts.push(`Slots filled at start: ${s.slots_filled_count}`);
  }
  return parts;
};

// Completion-specific meta parts for a `shift.completed` row: attendance
// (workers present, no-shows) and the final pay rate captured at completion. The
// event is system-emitted by the lifecycle sweep, so there is no human actor.
// Returns [] for other events. A no-show count of 0 is still shown so a clean
// shift reads "No-shows: 0" rather than omitting attendance entirely.
const completedParts = (entry: ShiftEventEntry): string[] => {
  const c: ShiftEventCompleted | null = entry.completed;
  if (!c) return [];
  const parts: string[] = [];
  if (c.workers_present_count != null) {
    parts.push(`Present: ${c.workers_present_count}`);
  }
  if (c.no_show_count != null) parts.push(`No-shows: ${c.no_show_count}`);
  if (c.final_pay_rate) parts.push(`Final pay: ${c.final_pay_rate}`);
  return parts;
};

// Human labels for the reminder channel (json_data.channel). Falls back to the
// raw value for any channel not mapped here.
const REMINDER_CHANNEL_LABELS: Record<string, string> = {
  push: 'Push',
  sms: 'SMS',
};

// Human labels for the reminder type (json_data.reminder_type). Falls back to
// the raw value for any type not mapped here.
const REMINDER_TYPE_LABELS: Record<string, string> = {
  checkin: 'Check-in',
  confirmation: 'Confirmation',
  reconfirmation: 'Reconfirmation',
};

// Reminder-specific meta parts for a `comms.reminder.sent` row: who the reminder
// went to, the channel (push/SMS), the reminder type (check-in/confirmation),
// and how long before the shift it fired. The event is automated/system-emitted,
// so the worker is surfaced here rather than on the actor line. Returns [] for
// other events.
const reminderParts = (entry: ShiftEventEntry): string[] => {
  const r: ShiftEventReminder | null = entry.reminder;
  if (!r) return [];
  const parts: string[] = [];
  if (r.worker) parts.push(`Worker: ${r.worker.name ?? `#${r.worker.id}`}`);
  if (r.channel) {
    parts.push(`Channel: ${REMINDER_CHANNEL_LABELS[r.channel] ?? r.channel}`);
  }
  if (r.reminder_type) {
    parts.push(
      `Type: ${REMINDER_TYPE_LABELS[r.reminder_type] ?? r.reminder_type}`,
    );
  }
  if (r.hours_before_shift) parts.push(r.hours_before_shift);
  return parts;
};

// Late-flag meta parts for a `shift.worker.late_flagged` row: which worker was
// flagged, how many minutes after the scheduled start the flag fired, and
// whether ops was alerted. The event is system-emitted by the late sweep, so the
// worker is surfaced here rather than on the actor line — this also lets several
// late-flag rows on the same shift be told apart by worker. Returns [] for other
// events.
const lateParts = (entry: ShiftEventEntry): string[] => {
  const l = entry.late;
  if (!l) return [];
  const parts: string[] = [];
  if (l.worker) parts.push(`Worker: ${l.worker.name ?? `#${l.worker.id}`}`);
  if (l.minutes_after_start != null) {
    parts.push(`${formatMinutes(l.minutes_after_start)} after start`);
  }
  if (l.ops_alerted != null) {
    parts.push(l.ops_alerted ? 'Ops alerted' : 'Ops not alerted');
  }
  return parts;
};

// The "flagged by" label for a no-show row: an ops admin (by name, falling back
// to an id) or the automated sweep ("System"). Null when no source is recorded.
const noShowFlaggedByLabel = (actor: ShiftEventActor | null): string | null => {
  if (!actor) return null;
  if (actor.type === 'system') return 'System';
  return actor.name ?? (actor.id != null ? `#${actor.id}` : null);
};

// No-show meta parts for a `shift.worker.no_show` row: which worker was flagged,
// who/what flagged it (an ops admin or the automated sweep), and how many minutes
// after the scheduled start the flag fired. The affected worker is never the row
// actor, so it is surfaced here — this also lets several no-show rows on the same
// shift be told apart by worker. Returns [] for other events.
const noShowParts = (entry: ShiftEventEntry): string[] => {
  const n = entry.no_show;
  if (!n) return [];
  const parts: string[] = [];
  if (n.worker) parts.push(`Worker: ${n.worker.name ?? `#${n.worker.id}`}`);
  const flaggedBy = noShowFlaggedByLabel(n.flagged_by);
  if (flaggedBy) parts.push(`Flagged by: ${flaggedBy}`);
  if (n.minutes_after_shift_start != null) {
    parts.push(`${formatMinutes(n.minutes_after_shift_start)} after start`);
  }
  return parts;
};

// Human labels for what triggered a pool expansion (json_data.trigger_source).
// Capture is deferred (Part B), so this is forward-compatible: any value not
// mapped here falls back to a humanized form of the raw source.
const POOL_TRIGGER_SOURCE_LABELS: Record<string, string> = {
  manual_tier_send: 'Manual tier send',
  scheduled_progression: 'Scheduled progression',
  worker_count_drop_reset: 'Worker-count drop reset',
  privacy_reset: 'Privacy reset',
  became_public: 'Became public',
};

// "manual_tier_send" -> "Manual tier send" when unmapped, so a future source
// value still reads cleanly instead of showing a raw snake_case token.
const humanizeSource = (source: string): string =>
  source.charAt(0).toUpperCase() + source.slice(1).replace(/_/g, ' ');

// Pool-expansion meta parts for a `shift.pool.expanded` row: how far the
// notification tier moved (previous -> new), the size of the newly-activated
// worker pool, and how long before the shift it fired (GRAVY-10196). trigger
// source and the manual-send actor are surfaced when present but stay absent
// until their capture (Part B) ships. Returns [] for other events.
const poolExpandedParts = (entry: ShiftEventEntry): string[] => {
  const p = entry.pool_expanded;
  if (!p) return [];
  const parts: string[] = [];
  if (p.previous_tier != null && p.new_tier != null) {
    parts.push(`Tier: ${p.previous_tier} → ${p.new_tier}`);
  } else if (p.new_tier != null) {
    parts.push(`Tier: ${p.new_tier}`);
  }
  if (p.new_tier_pool_size != null) {
    parts.push(`New pool size: ${p.new_tier_pool_size}`);
  }
  if (p.hours_before_shift_start) parts.push(p.hours_before_shift_start);
  if (p.trigger_source) {
    parts.push(
      `Source: ${
        POOL_TRIGGER_SOURCE_LABELS[p.trigger_source] ??
        humanizeSource(p.trigger_source)
      }`,
    );
  }
  if (p.triggered_by?.type === 'ops') {
    const who =
      p.triggered_by.name ??
      (p.triggered_by.id != null ? `#${p.triggered_by.id}` : null);
    if (who) parts.push(`Sent by: ${who}`);
  }
  return parts;
};

// Accept meta parts for a `shift.invitation.accepted` row: the worker and HOW
// they accepted — a direct invitation accept vs. claiming an open offer — via the
// user-friendly label computed server-side. The worker is also the row actor
// (worker-initiated), but is surfaced here for a self-contained block. Returns []
// for other events.
const invitationAcceptedParts = (entry: ShiftEventEntry): string[] => {
  const a = entry.invitation_accepted;
  if (!a) return [];
  const parts: string[] = [];
  if (a.worker) parts.push(`Worker: ${a.worker.name ?? `#${a.worker.id}`}`);
  if (a.claimed_via_label) parts.push(`Via: ${a.claimed_via_label}`);
  return parts;
};

// Rating meta parts for an `ops.worker.rated` row: the rated worker, who rated
// them (an ops/client admin), the rating value, the reason/comment, and any tags.
// The rating admin is also the row actor but is surfaced here for a self-contained
// block. Optional fields (reason, tags) are omitted when empty so the row never
// shows a blank label. Returns [] for other events.
const workerRatingParts = (entry: ShiftEventEntry): string[] => {
  const r = entry.worker_rating;
  if (!r) return [];
  const parts: string[] = [];
  if (r.worker) parts.push(`Worker: ${r.worker.name ?? `#${r.worker.id}`}`);
  if (r.rated_by) {
    parts.push(`Rated by: ${r.rated_by.name ?? `#${r.rated_by.id}`}`);
  }
  if (r.rating != null) parts.push(`Rating: ${r.rating}`);
  if (r.rating_reason) parts.push(`Reason: ${r.rating_reason}`);
  if (r.tags.length > 0) parts.push(`Tags: ${r.tags.join(', ')}`);
  return parts;
};

// Pay-rate meta parts for a `shift.pay_rate.changed` row: the pricing rule_id that
// drove an automated change, appended after the old -> new rate already rendered
// from `changes`. rule_id is null for a manual change (the actor line carries the
// source), so it is omitted rather than shown as a blank label. Returns [] for
// other events.
const payRateParts = (entry: ShiftEventEntry): string[] => {
  const p = entry.pay_rate;
  if (!p) return [];
  const parts: string[] = [];
  if (p.rule_id) parts.push(`Rule: ${p.rule_id}`);
  return parts;
};

// The job-details page for the event's shift, e.g. "/orders/12/jobs/34".
// Requires both ids (a job lives under an order); null when either is missing.
const jobDetailPath = (entry: ShiftEventEntry): string | null => {
  const { shift } = entry;
  if (!shift?.order_id || !shift.job_id) return null;
  return `/orders/${shift.order_id}/jobs/${shift.job_id}`;
};

// The clickable job id, e.g. "#34" → opens the job-details page in a new tab.
const JobIdLink = ({ entry }: { entry: ShiftEventEntry }) => {
  const jobId = entry.shift?.job_id;
  if (jobId == null) return null;
  const path = jobDetailPath(entry);
  const label = `#${jobId}`;

  if (!path) {
    return (
      <Text weight="semibold" css={{ color: GREEN }}>
        {label}
      </Text>
    );
  }
  return (
    <Link
      to={path}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: GREEN, fontWeight: 600, textDecoration: 'none' }}
    >
      {label}
    </Link>
  );
};

export const EventRow = ({ entry }: { entry: ShiftEventEntry }) => {
  const changeParts = entry.changes.map(
    (c) => `${c.field} ${c.from ?? '—'} → ${c.to ?? '—'}`,
  );
  return (
    <Stack
      vertical
      gap={8}
      align="start"
      style={{
        borderBottom: `1px solid ${BORDER}`,
        paddingBottom: 16,
        width: '100%',
      }}
    >
      <Stack gap={8} align="baseline" inline>
        <Text as="p" size="md" weight="bold" css={{ color: BLACK }}>
          {entry.description}
        </Text>
        <JobIdLink entry={entry} />
      </Stack>

      <Stack align="end" justify="apart" style={{ gap: 16 }}>
        <Stack vertical gap={4} align="start" style={{ flex: 1, minWidth: 0 }}>
          <PrimaryLine entry={entry} />
          <MetaRow
            parts={[
              formatShiftWindow(entry),
              entry.shift?.location,
              formatFill(entry),
              entry.shift?.pay_rate,
              ...cancellationParts(entry),
              ...invitationParts(entry),
              ...expirationParts(entry),
              ...hireParts(entry),
              ...reasonParts(entry),
              ...backfillParts(entry),
              ...backfillFillParts(entry),
              ...checkinParts(entry),
              ...checkoutParts(entry),
              ...startedParts(entry),
              ...completedParts(entry),
              ...reminderParts(entry),
              ...lateParts(entry),
              ...noShowParts(entry),
              ...poolExpandedParts(entry),
              ...invitationAcceptedParts(entry),
              ...workerRatingParts(entry),
              ...payRateParts(entry),
              ...changeParts,
            ]}
          />
        </Stack>
        <Text
          as="span"
          size="sm"
          css={{ color: GRAY, whiteSpace: 'nowrap', flexShrink: 0 }}
        >
          {formatActorLine(entry)}
        </Text>
      </Stack>
    </Stack>
  );
};

const ActionsLog = () => {
  const [rows, setRows] = useState<ShiftEventEntry[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [search, setSearch] = useState('');
  const [userId, setUserId] = useState('');
  const [clientId, setClientId] = useState('');
  const [date, setDate] = useState('');
  // A native <input type="date"> can't show a custom "Select date" placeholder,
  // so render an empty, unfocused date field as text (placeholder visible) and
  // switch to the native date picker on focus or once a value is chosen.
  const [dateFocused, setDateFocused] = useState(false);

  // Dropdown options (only users/clients present in the feed)
  const [users, setUsers] = useState<ShiftEventFilterOption[]>([]);
  const [clients, setClients] = useState<ShiftEventFilterOption[]>([]);

  useEffect(() => {
    fetchShiftEventFilters()
      .then((res) => {
        setUsers(res.data.users);
        setClients(res.data.clients);
      })
      .catch(() => {
        // Non-fatal: the feed still works without dropdown options.
      });
  }, []);

  // Single debounced fetch keyed on all filters + page. Filter changes reset to
  // page 1 (handlers below), so page === 1 means "replace", otherwise "append".
  useEffect(() => {
    let cancelled = false;
    setError(null);
    setFetching(true);

    const timer = setTimeout(
      () => {
        fetchShiftEvents({
          page,
          per_page: PER_PAGE,
          search: search || undefined,
          user_id: userId || undefined,
          client_id: clientId || undefined,
          date: date || undefined,
        })
          .then((res) => {
            if (cancelled) return;
            setRows((prev) => (page === 1 ? res.data : [...prev, ...res.data]));
            setTotalPages(res.pagination.total_pages);
          })
          .catch((e: unknown) => {
            if (cancelled) return;
            setError(
              e instanceof Error ? e.message : 'Failed to load operations log',
            );
          })
          .finally(() => {
            if (!cancelled) setFetching(false);
          });
      },
      search ? 300 : 0,
    );

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, userId, clientId, date, page]);

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(1);
  };
  const onUser = (e: SelectChangeEvent<string>) => {
    setUserId(e.target.value);
    setPage(1);
  };
  const onClient = (e: SelectChangeEvent<string>) => {
    setClientId(e.target.value);
    setPage(1);
  };
  const onDate = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDate(e.target.value);
    setPage(1);
  };
  const clearFilters = () => {
    setSearch('');
    setUserId('');
    setClientId('');
    setDate('');
    setPage(1);
  };

  const hasFilters = Boolean(search || userId || clientId || date);
  const hasMore = page < totalPages;

  return (
    <Page size="full" title="Operations log">
      <Stack vertical gap={24} align="start">
        {/* Filter bar: search + segmented user / client / date controls */}
        <Stack gap={16} wrap style={{ alignItems: 'stretch' }}>
          <TextField
            size="small"
            value={search}
            onChange={onSearch}
            placeholder="Search events or IDs"
            style={{ minWidth: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FontAwesomeIcon
                    icon={faMagnifyingGlass}
                    style={{ color: '#979797', fontSize: 16 }}
                  />
                </InputAdornment>
              ),
            }}
          />
          <Stack
            inline
            style={{
              border: `1px solid ${BORDER}`,
              borderRadius: 8,
              overflow: 'hidden',
            }}
          >
            <Select
              size="small"
              value={userId}
              onChange={onUser}
              displayEmpty
              variant="standard"
              disableUnderline
              style={{ width: 160, padding: '0 8px 0 16px' }}
            >
              <MenuItem value="">All users</MenuItem>
              {users.map((u) => (
                <MenuItem key={u.id} value={String(u.id)}>
                  {u.name}
                </MenuItem>
              ))}
            </Select>
            <div
              style={{ width: 1, alignSelf: 'stretch', background: BORDER }}
            />
            <Select
              size="small"
              value={clientId}
              onChange={onClient}
              displayEmpty
              variant="standard"
              disableUnderline
              style={{ width: 160, padding: '0 8px 0 16px' }}
            >
              <MenuItem value="">All clients</MenuItem>
              {clients.map((c) => (
                <MenuItem key={c.id} value={String(c.id)}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
            <div
              style={{ width: 1, alignSelf: 'stretch', background: BORDER }}
            />
            <TextField
              size="small"
              type={dateFocused || date ? 'date' : 'text'}
              value={date}
              onChange={onDate}
              onFocus={() => setDateFocused(true)}
              onBlur={() => setDateFocused(false)}
              placeholder="Select date"
              variant="standard"
              InputProps={{ disableUnderline: true }}
              style={{ width: 160, padding: '0 16px' }}
            />
          </Stack>
          {hasFilters && (
            <button
              type="button"
              onClick={clearFilters}
              style={{
                background: 'none',
                border: 'none',
                color: GREEN,
                cursor: 'pointer',
                fontSize: 14,
                padding: '0 4px',
              }}
            >
              Clear filters
            </button>
          )}
        </Stack>

        {error ? (
          <Body color="lighter">{error}</Body>
        ) : (
          <Stack vertical gap={16} align="start">
            {rows.map((entry) => (
              <EventRow key={entry.id} entry={entry} />
            ))}

            {rows.length === 0 && !fetching && (
              <Stack justify="center" style={{ padding: 40 }}>
                <Body color="lighter">No events found.</Body>
              </Stack>
            )}

            {fetching && (
              <Stack justify="center" style={{ padding: 24 }}>
                <CircularProgress size={24} />
              </Stack>
            )}

            {hasMore && !fetching && (
              <Stack justify="center" style={{ paddingTop: 8 }}>
                <button
                  type="button"
                  onClick={() => setPage((p) => p + 1)}
                  style={{
                    background: 'none',
                    border: `1px solid ${BORDER}`,
                    borderRadius: 8,
                    cursor: 'pointer',
                    padding: '8px 16px',
                    color: BLACK,
                    fontSize: 14,
                  }}
                >
                  Load more
                </button>
              </Stack>
            )}
          </Stack>
        )}
      </Stack>
    </Page>
  );
};

export default ActionsLog;
